import { logError } from "@/lib/log";
import { useEffect, useState, useCallback } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Download, Mail } from "lucide-react";
import { SpendingInsights } from "@/components/SpendingInsights";
import { NarrativeCard } from "@/components/reports/NarrativeCard";
import { CashflowSection } from "@/components/reports/CashflowSection";
import { MonthlyCharts } from "@/components/reports/MonthlyCharts";
import { CategoryCharts } from "@/components/reports/CategoryCharts";
import { CategoryDeepDive } from "@/components/reports/CategoryDeepDive";
import { DematSection } from "@/components/reports/DematSection";

export default function Reports() {
  const [cashflow, setCashflow] = useState([]);
  const [cats, setCats] = useState([]);
  const [catTrend, setCatTrend] = useState(null);
  const [demat, setDemat] = useState(null);
  const [range, setRange] = useState("month");
  const [narrative, setNarrative] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [cf, cat, d, ct] = await Promise.all([
        api.get("/reports/cashflow?months=12"),
        api.get(`/reports/categories?range=${range}`),
        api.get("/reports/demat"),
        api.get("/reports/category-trend?months=12&top=5"),
      ]);
      setCashflow(cf.data);
      setCats(cat.data);
      setDemat(d.data);
      setCatTrend(ct.data);
    } catch (e) {
      logError("Reports load failed", e);
      toast.error("Failed to load reports");
    }
  }, [range]);
  useEffect(() => { loadAll(); }, [loadAll]);

  const generateNarrative = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/ai/report");
      setNarrative(data.report);
    } catch { toast.error("AI report failed"); }
    finally { setAiBusy(false); }
  };

  const downloadCSV = () => {
    fetch(`${API}/reports/transactions.csv`, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "finledger-transactions.csv";
        link.click();
        toast.success("CSV downloaded");
      })
      .catch(() => toast.error("Download failed"));
  };

  const [digestBusy, setDigestBusy] = useState(false);
  const emailDigest = async () => {
    setDigestBusy(true);
    try {
      const { data } = await api.post("/digest/send");
      toast.success(`${data.period} digest sent to ${data.sent_to}`);
    } catch {
      toast.error("Could not send the digest — try again shortly");
    } finally {
      setDigestBusy(false);
    }
  };

  return (
    <div className="space-y-10">
      <div className="flex items-end justify-between border-b border-[#1A1F16] pb-6 flex-wrap gap-4">
        <div>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#6C7565]">Section VI</span>
          <h2 className="font-serif text-5xl tracking-tight mt-2">Reports.</h2>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="email-digest-btn"
            onClick={emailDigest}
            disabled={digestBusy}
            className="border border-[#1A1F16] px-4 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-[#1A1F16] hover:text-[#F7F5F0] flex items-center gap-2 disabled:opacity-50"
          >
            <Mail className="w-3.5 h-3.5" /> {digestBusy ? "Sending…" : "Email digest"}
          </button>
          <button
            data-testid="download-csv-btn"
            onClick={downloadCSV}
            className="border border-[#1A1F16] px-4 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-[#1A1F16] hover:text-[#F7F5F0] flex items-center gap-2"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <button
            data-testid="generate-report-btn"
            onClick={generateNarrative}
            disabled={aiBusy}
            className="bg-[#1A1F16] text-[#F7F5F0] px-4 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-[#2C3628] flex items-center gap-2 disabled:opacity-50"
          >
            <Sparkles className="w-3.5 h-3.5" /> {aiBusy ? "Drafting…" : "AI narrative"}
          </button>
        </div>
      </div>

      <NarrativeCard narrative={narrative} />

      <CashflowSection cashflow={cashflow} />

      <section>
        <div className="border-b border-[#D8D4C7] pb-4 mb-6">
          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#6C7565]">Analytics</div>
          <h3 className="font-serif text-3xl tracking-tight">Charts.</h3>
        </div>
        <MonthlyCharts cashflow={cashflow} />
        <CategoryCharts catTrend={catTrend} cats={cats} />
        <div className="mt-6">
          <SpendingInsights variant="category" kicker="GPT-5.4 Mini · Auto-analysis" heading="Category intelligence." testid="category-intelligence" />
        </div>
      </section>

      <CategoryDeepDive cats={cats} range={range} setRange={setRange} />

      <DematSection demat={demat} />
    </div>
  );
}
